import infoBtn from "assets/images/info_btn.svg";
import OrganisationAuditWindow from "components/Audit/OrganisationAuditWindow";
import Status from "components/General/Status";
import SvgBtn from "components/General/SvgBtn";
import Alert from "components/General/Alert";
import Loader from "components/Loaders/Loader";
import { ReactComponent as WarningIcon } from "assets/symbols/warning.svg";
import { fetchHandleGetAccounts } from "helpers/Account/AccountHelpers";
import { TokenContext } from "helpers/constants/Contexts";
import { IAccount } from "interfaces/Account/IAccount";
import { IAccountsState } from "interfaces/Account/IAccountsState";
import { IGetAccountsRequest } from "interfaces/Account/IGetAccountsRequest";
import { IButtonProps } from "interfaces/General/IButtonProps";
import { ILicenceState } from "interfaces/Licence/ILicenseState";
import { IOrganisation } from "interfaces/Organisation/IOrganisation";
import { IOrganisationsState } from "interfaces/Organisation/IOrganisationsState";
import { ReactNode, useContext, useEffect, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  accountsStateSelector,
  licensesStateSelector,
  organisationsStateSelector,
} from "redux/states/stateSelectors";
import AccountsList from "../../components/Account/AccountsList";
import PageHeader from "../../components/Headers/PageHeader";
import ModalComponent from "../../components/Modal/ModalComponent";
import OrganisationDetailComponent from "../../components/Organisation/OrganisationDetail";
import {
  fetchHandleDeleteOrganisation,
  fetchHandleSuspendLicence,
  findOrganisation,
} from "../../helpers/Organisations/OrganisationHelpers";

const Organisation = () => {
  //#region react-redux
  const dispatch = useDispatch();
  //#endregion

  //#region react-router-dom
  const { orgId } = useParams();
  const navigate = useNavigate();
  //#endregion

  //#region states
  const organisationState: IOrganisationsState = useSelector(
    organisationsStateSelector
  );
  const accountsState: IAccountsState = useSelector(accountsStateSelector);
  const licenceState: ILicenceState = useSelector(licensesStateSelector);
  //#endregion

  //#region useStates
  const [activeTab, setActiveTab] = useState(
    sessionStorage.getItem("orgLastActiveTab")
  );
  const [displayModal, setDisplayModal] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalBody, setModalBody] = useState<string | ReactNode>("");
  const [selectedLicenceTypeId, setSelectedLicenceTypeId] = useState(null);
  const [organisation, setOrganisation] = useState<IOrganisation>(
    findOrganisation(orgId!, organisationState.organisations)!
  );
  const [organisationAccountsState, setOrganisationAccountsState] =
    useState<IAccountsState>({ accounts: [], loading: true });
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  //#endregion

  //#region useEffects
  useEffect(() => {
    if (activeTab !== null || undefined) {
      sessionStorage.setItem("orgLastActiveTab", activeTab!);
    } else {
      setActiveTab("organisationdetails");
    }
  }, [activeTab]);

  useEffect(() => {
    if (!organisation) {
      navigate("/organisations");
    }
    let filteredOrgAccounts: IAccount[] = [];
    if (accountsState.accounts != null) {
      accountsState.accounts.forEach((account) => {
        if (
          account.organisation != null &&
          account.organisation.organisationId === orgId
        ) {
          filteredOrgAccounts.push(account);
        }
      });
    }
    let state: IAccountsState = {
      accounts: filteredOrgAccounts,
      loading: false,
    };
    setOrganisationAccountsState(state);
    setAccountsLoading(accountsState.loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, accountsState]);
  //#endregion

  //#region constants
  const accessToken = useContext(TokenContext);
  const linkBtns: IButtonProps[] = [
    {
      text: organisation?.suspended ? "Unsuspend licence" : "Suspend licence",
      disabled: organisation?.archived,
      onClick: () => {
        const toSuspend = organisation?.suspended ? "Unsuspend" : "Suspend";
        setModalAction(toSuspend);
        setModalTitle(`Warning: ${toSuspend} license`);
        setModalBody(
          `Are you sure you want to ${toSuspend.toLowerCase()} license?`
        );
        setDisplayModal(true);
      },
    },
    {
      text: "Delete organisation",
      disabled: organisation?.archived,
      onClick: () => {
        setModalAction("Delete");
        setModalTitle("Warning: Delete organisation");
        setModalBody(
          <div className="text-label-3 gap-4 flex flex-col">
            <span>
              Are you sure you want to delete
              <b> {organisation?.organisationName}</b> and all associated
              accounts?
            </span>
            <Alert icon={<WarningIcon />} variant="warning">
              This action cannot be undone.
            </Alert>
          </div>
        );
        setDisplayModal(true);
      },
    },
  ];
  //#endregion

  //#region functions
  const handleCloseModal = () => {
    setDisplayModal(false);
  };

  const handleSelectedLicenceType = (selectedLicenceTypeId) => {
    setSelectedLicenceTypeId(selectedLicenceTypeId);
    setActiveTab("useraccounts");
  };

  const modalClicked = (action) => {
    if (action === "Suspend" || modalAction === "Unsuspend") {
      dispatch(
        fetchHandleSuspendLicence(
          accessToken,
          organisation,
          (org) => {
            setOrganisation(org);
            setDisplayModal(false);
            const accountsRequest: IGetAccountsRequest = {
              OrganisationId: orgId!,
              LicenceTypeId: null,
              SortBy: null,
              SearchValue: null,
            };
            dispatch(fetchHandleGetAccounts(accessToken, accountsRequest));
          },
          (message) => {
            //need to do some actual error handling
            console.log(message);
          }
        )
      );
    } else if (modalAction === "Delete") {
      dispatch(
        fetchHandleDeleteOrganisation(
          accessToken,
          organisation?.organisationId,
          (org) => {
            setDisplayModal(false);
            navigate("/");
            const accountsRequest: IGetAccountsRequest = {
              OrganisationId: orgId!,
              LicenceTypeId: null,
              SortBy: null,
              SearchValue: null,
            };
            dispatch(fetchHandleGetAccounts(accessToken, accountsRequest));
          },
          (message) => {
            //need to do some actual error handling
            console.log(message);
          }
        )
      );
    }
  };
  //#endregion

  return (
    <div className="flex flex-col gap-3">
      {/* <BackHeader navigateUrl={"/organisations"} /> */}
      <PageHeader
        title={`${organisation?.organisationName}`}
        spanBtn={{
          text: "Edit Organisation",
          onClick: () =>
            navigate(`/organisations/edit/${organisation?.organisationId}`),
        }}
        linkBtns={linkBtns}
        backLink="/organisations"
      />
      <div className="px-10 flex flex-row gap-10 items-center text-white">
        <label className="text-label-2">Status:</label>
        <Status
          suspended={organisation?.suspended}
          archived={organisation?.archived}
        />
        <SvgBtn
          variant="white"
          icon={infoBtn}
          onClick={() => setShowAudit(true)}
        />
      </div>
      <Tabs
        defaultActiveKey="organisations"
        activeKey={activeTab!}
        // @ts-ignore
        onSelect={(tabKey) => setActiveTab(tabKey)}
        id="mainTabs"
        variant="pills"
        className="px-10"
      >
        <Tab eventKey="organisationdetails" title="Organisation detail">
          <OrganisationDetailComponent
            organisation={organisation}
            handleDisplayAccounts={handleSelectedLicenceType}
          />
        </Tab>
        <Tab eventKey="useraccounts" title={"User accounts"}>
          {accountsLoading ? (
            <Loader />
          ) : (
            <AccountsList
              activeTab={activeTab}
              filterPrefix={organisation?.organisationId}
              accountsState={organisationAccountsState}
              selectedLicenceTypeId={selectedLicenceTypeId}
              licenceTypes={licenceState.licences}
              addAccountUrl={`/organisations/${orgId}/accounts/add`}
              editAccountUrl={`/organisations/${orgId}/accounts/edit/`}
              disabled={organisation?.archived}
            />
          )}
        </Tab>
      </Tabs>
      <ModalComponent
        actionHandler={modalClicked}
        handleClose={handleCloseModal}
        displayModal={displayModal}
        action={modalAction}
        modalTitle={modalTitle}
        modalBody={modalBody}
        size={450}
      />
      <OrganisationAuditWindow
        organisation={organisation}
        show={showAudit}
        handleClose={() => setShowAudit(false)}
      />
    </div>
  );
};

export default Organisation;
